import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, useAuth } from '../../App';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import ImageUploader from '../../components/ImageUploader';
import { 
  Plus, 
  Pencil, 
  Trash2, 
  Bell, 
  AlertTriangle,
  Eye,
  EyeOff,
  Calendar,
  Link as LinkIcon,
  Megaphone
} from 'lucide-react';

const AdminPopups = () => {
  const { token } = useAuth();
  const [popups, setPopups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    type: 'custom',
    priority: 'normal',
    image_url: '',
    link_url: '',
    link_text: '',
    start_date: '',
    end_date: '',
    is_active: true
  });

  const fetchPopups = async () => {
    try {
      const response = await axios.get(`${API}/admin/popups`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPopups(response.data);
    } catch (error) {
      console.error('Failed to fetch popups:', error);
      toast.error('Failed to load popups');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPopups();
  }, [token]);

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      type: 'custom',
      priority: 'normal',
      image_url: '',
      link_url: '',
      link_text: '',
      start_date: '',
      end_date: '',
      is_active: true
    });
    setEditing(null);
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.content) {
      toast.error('Please fill in title and content');
      return;
    }

    try {
      if (editing) {
        await axios.put(`${API}/admin/popups/${editing}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Popup updated successfully!');
      } else {
        await axios.post(`${API}/admin/popups`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Popup created successfully!');
      }
      setShowDialog(false);
      resetForm();
      fetchPopups();
    } catch (error) {
      console.error('Failed to save popup:', error);
      toast.error('Failed to save popup');
    }
  };

  const handleEdit = (popup) => {
    setFormData({
      title: popup.title,
      content: popup.content,
      type: popup.type,
      priority: popup.priority,
      image_url: popup.image_url || '',
      link_url: popup.link_url || '',
      link_text: popup.link_text || '',
      start_date: popup.start_date || '',
      end_date: popup.end_date || '',
      is_active: popup.is_active
    });
    setEditing(popup.id);
    setShowDialog(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this popup?')) return;
    
    try {
      await axios.delete(`${API}/admin/popups/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Popup deleted!');
      fetchPopups();
    } catch (error) {
      toast.error('Failed to delete popup');
    }
  };

  const handleToggleActive = async (popup) => {
    try {
      await axios.patch(`${API}/admin/popups/${popup.id}/toggle`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Popup ${popup.is_active ? 'deactivated' : 'activated'}!`);
      fetchPopups();
    } catch (error) {
      toast.error('Failed to update popup');
    }
  };

  const getPriorityBadge = (priority) => {
    switch (priority) {
      case 'urgent':
        return <Badge className="bg-red-500 text-white">Urgent</Badge>;
      case 'high':
        return <Badge className="bg-orange-500 text-white">High</Badge>;
      default:
        return <Badge className="bg-slate-500 text-white">Normal</Badge>;
    }
  };

  const getTypeBadge = (type) => {
    switch (type) {
      case 'announcement':
        return <Badge className="bg-plp-blue text-white">Announcement</Badge>;
      case 'event':
        return <Badge className="bg-purple-500 text-white">Event</Badge>;
      case 'urgent':
        return <Badge className="bg-red-500 text-white">Urgent Alert</Badge>;
      default:
        return <Badge className="bg-plp-gold text-plp-blue">Custom</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-plp-blue"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-popups">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Custom Popups</h1>
          <p className="text-slate-600">Create and manage site-wide popup notifications</p>
        </div>
        <Dialog open={showDialog} onOpenChange={(open) => {
          setShowDialog(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button className="bg-plp-blue hover:bg-plp-blue-light" data-testid="create-popup-btn">
              <Plus className="w-4 h-4 mr-2" />
              Create Popup
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editing ? 'Edit Popup' : 'Create New Popup'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  placeholder="Enter popup title"
                  data-testid="popup-title-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="content">Content *</Label>
                <Textarea
                  id="content"
                  value={formData.content}
                  onChange={(e) => setFormData({...formData, content: e.target.value})}
                  placeholder="Enter popup message"
                  rows={4}
                  data-testid="popup-content-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Type</Label>
                  <Select 
                    value={formData.type} 
                    onValueChange={(value) => setFormData({...formData, type: value})}
                  >
                    <SelectTrigger data-testid="popup-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="custom">Custom</SelectItem>
                      <SelectItem value="announcement">Announcement</SelectItem>
                      <SelectItem value="event">Event</SelectItem>
                      <SelectItem value="urgent">Urgent Alert</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Priority</Label>
                  <Select 
                    value={formData.priority} 
                    onValueChange={(value) => setFormData({...formData, priority: value})}
                  >
                    <SelectTrigger data-testid="popup-priority-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="normal">Normal</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <ImageUploader
                value={formData.image_url}
                onChange={(url) => setFormData({...formData, image_url: url})}
                label="Popup Image (optional)"
              />

              <div className="space-y-2">
                <Label htmlFor="link_url">Link URL (optional)</Label>
                <Input
                  id="link_url"
                  value={formData.link_url}
                  onChange={(e) => setFormData({...formData, link_url: e.target.value})}
                  placeholder="https://example.com"
                  data-testid="popup-link-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="start_date">Start Date (optional)</Label>
                  <Input
                    id="start_date"
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                    data-testid="popup-start-date"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="end_date">End Date (optional)</Label>
                  <Input
                    id="end_date"
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                    data-testid="popup-end-date"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between bg-slate-50 p-3 rounded-lg">
                <div>
                  <Label>Active Status</Label>
                  <p className="text-sm text-slate-500">Show this popup to visitors</p>
                </div>
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({...formData, is_active: checked})}
                  data-testid="popup-active-switch"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setShowDialog(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleSubmit}
                  className="bg-plp-blue hover:bg-plp-blue-light"
                  data-testid="popup-submit-btn"
                >
                  {editing ? 'Update Popup' : 'Create Popup'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-plp-blue/10 rounded-xl flex items-center justify-center">
                <Bell className="w-6 h-6 text-plp-blue" />
              </div>
              <div>
                <p className="text-2xl font-bold">{popups.length}</p>
                <p className="text-sm text-slate-500">Total Popups</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-emerald-500/10 rounded-xl flex items-center justify-center">
                <Eye className="w-6 h-6 text-emerald-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{popups.filter(p => p.is_active).length}</p>
                <p className="text-sm text-slate-500">Active Popups</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-red-500/10 rounded-xl flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{popups.filter(p => p.priority === 'urgent').length}</p>
                <p className="text-sm text-slate-500">Urgent Popups</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Popups List */}
      <Card>
        <CardHeader>
          <CardTitle>All Popups</CardTitle>
        </CardHeader>
        <CardContent>
          {popups.length === 0 ? (
            <div className="text-center py-12">
              <Bell className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No popups yet. Create your first popup!</p>
            </div>
          ) : (
            <div className="space-y-4">
              {popups.map((popup) => (
                <div 
                  key={popup.id}
                  className={`border rounded-lg p-4 ${popup.is_active ? 'bg-white' : 'bg-slate-50 opacity-75'}`}
                  data-testid={`popup-item-${popup.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-semibold text-lg">{popup.title}</h3>
                        {getTypeBadge(popup.type)}
                        {getPriorityBadge(popup.priority)}
                        {!popup.is_active && (
                          <Badge variant="outline" className="text-slate-500">Inactive</Badge>
                        )}
                      </div>
                      <p className="text-slate-600 line-clamp-2 mb-2">{popup.content}</p>
                      <div className="flex items-center gap-4 text-sm text-slate-500">
                        {popup.link_url && (
                          <span className="flex items-center gap-1">
                            <LinkIcon className="w-3 h-3" />
                            Has link
                          </span>
                        )}
                        {(popup.start_date || popup.end_date) && (
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {popup.start_date || 'Now'} - {popup.end_date || 'Indefinite'}
                          </span>
                        )}
                        <span>
                          Created: {new Date(popup.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    
                    {popup.image_url && (
                      <div className="ml-4 w-20 h-20 rounded-lg overflow-hidden flex-shrink-0">
                        <img 
                          src={popup.image_url.startsWith('/api') 
                            ? `${API.replace('/api', '')}${popup.image_url}` 
                            : popup.image_url
                          }
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-2 mt-4 pt-4 border-t">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleToggleActive(popup)}
                      data-testid={`popup-toggle-${popup.id}`}
                    >
                      {popup.is_active ? (
                        <>
                          <EyeOff className="w-4 h-4 mr-1" />
                          Deactivate
                        </>
                      ) : (
                        <>
                          <Eye className="w-4 h-4 mr-1" />
                          Activate
                        </>
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(popup)}
                      data-testid={`popup-edit-${popup.id}`}
                    >
                      <Pencil className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(popup.id)}
                      className="text-red-500 hover:text-red-700"
                      data-testid={`popup-delete-${popup.id}`}
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Delete
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminPopups;
